import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./../../../assets/css/admin/gestionCategorie/AdminCategoriesPage.css";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";

import useCategorie from "../../../hooks/useCategorie.js";
import useGroupeCategorie from "../../../hooks/useGroupeCategorie.js";
import IconPicker from "../../../components/icon/IconPicker.jsx";

export default function CategorieCreatePage({ setAlert }) {
  const navigate = useNavigate();
  const { createCategorie } = useCategorie();
  const { fetchGroupes, groupes } = useGroupeCategorie();
  const [loading, setLoading] = useState(false);
  const [groupesLocal, setGroupesLocal] = useState([]);
  const location = useLocation();
  const suggestedRang = location.state?.suggestedRang ?? 1;
  const [latsRang, setLastRang] = useState(suggestedRang);
  const [form, setForm] = useState({
    nom: "",
    archive: 0,
    idCategorieGroupe: "",
    rang: latsRang,
    fields: [],
    premium: 2, // 1 = Premium, 2 = Standard
    hasPrice: 1, // 1 = Oui, 2 = Non
    prixAbonnement: 0,
    prixBoost: 0,
  });

  const breadcrumbItems = [
    { label: "Mon Compte", link: "/utilisateur/compte" },
    { label: "Catégories", link: "/categories" },
    { label: "Création" },
  ];

  useEffect(() => {
    const loadGroupes = async () => {
      try {
        const res = await fetchGroupes?.();
        const listFromRes = res?.data?.data ?? res?.data ?? res;
        if (Array.isArray(listFromRes)) {
          setGroupesLocal(listFromRes);
        }
      } catch (e) {
        console.error(e);
        setGroupesLocal([]);
        setAlert?.({
          type: "danger",
          message: "Impossible de charger les groupes de catégories.",
        });
      }
    };
    loadGroupes();
  }, []);

  const groupesList = useMemo(() => {
    if (Array.isArray(groupes) && groupes.length > 0) return groupes;
    if (Array.isArray(groupesLocal)) return groupesLocal;
    return [];
  }, [groupes, groupesLocal]);

  const addField = () => {
    setForm((prev) => ({
      ...prev,
      fields: [...(prev.fields || []), { name: "", icon: "" }],
    }));
  };

  const updateField = (index, key, value) => {
    setForm((prev) => {
      const newFields = [...(prev.fields || [])];
      newFields[index] = { ...newFields[index], [key]: value };
      return { ...prev, fields: newFields };
    });
  };

  const removeField = (index) => {
    setForm((prev) => ({
      ...prev,
      fields: (prev.fields || []).filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const cleanedFields = (form.fields || []).map((f) => ({
        name: String(f?.name ?? "").trim(),
        icon: String(f?.icon ?? "").trim(),
      }));

      const payload = {
        ...form,
        rang: Number(form.rang ?? 0),
        fields: cleanedFields,
      };

      await createCategorie(payload);

      setAlert?.({ type: "success", message: "Catégorie créée avec succès." });
      navigate("/admin/categories");
    } catch (err) {
      console.error(err);
      setAlert?.({
        type: "danger",
        message: "Impossible de créer la catégorie.",
      });
    } finally {
      setLoading(false);
    }
  };

  const changePreminumByIdGroupCategory = (value) => {
    groupes.forEach((groupe) => {
      if (groupe._id == value) {
        setForm((prev) => ({
          ...prev,
          premium: groupe.type,
        }));
      }
    });
  };

  return (
    <div className="adminCategoriesPage">
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="adminCategoriesGrid">
          <section className="adminCategoriesCard adminCategoriesCardCenter">
            <div className="adminCategoriesHeader">
              <div className="adminCategoriesHeaderLeft">
                <div className="adminCategoriesTitle">Créer une catégorie</div>
                <div className="adminCategoriesSubtitle">
                  Remplissez le formulaire, puis enregistrez.
                </div>
              </div>

              <div className="adminCategoriesHeaderRight">
                <Link
                  to="/admin/categories"
                  className="adminCategoriesBtn adminCategoriesBtnSoft"
                  title="Retour"
                >
                  <i className="bi bi-arrow-left" />
                  <span>Retour</span>
                </Link>

                <button
                  type="submit"
                  form="adminCategoriesForm"
                  className="adminCategoriesBtn adminCategoriesBtnPrimary"
                  disabled={loading}
                  title="Enregistrer"
                >
                  <i className="bi bi-check2-circle" />
                  <span>{loading ? "Enregistrement..." : "Enregistrer"}</span>
                </button>
              </div>
            </div>

            <form
              id="adminCategoriesForm"
              className="adminCategoriesForm"
              onSubmit={handleSubmit}
            >
              <div className="adminCategoriesFormGrid">
                <div className="adminCategoriesFormGroup">
                  <label className="adminCategoriesLabel">
                    Nom de la catégorie
                  </label>
                  <input
                    className="adminCategoriesInput"
                    value={form.nom}
                    onChange={(e) => setForm({ ...form, nom: e.target.value })}
                    placeholder="Ex: Électronique"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="adminCategoriesFormGroup">
                  <label className="adminCategoriesLabel">Groupe</label>
                  <select
                    className="adminCategoriesSelect"
                    value={form.idCategorieGroupe}
                    onChange={(e) => {
                      setForm({
                        ...form,
                        idCategorieGroupe: e.target.value,
                      });
                      changePreminumByIdGroupCategory(e.target.value);
                    }}
                    required
                    disabled={loading}
                  >
                    <option value="">-- Groupe de catégorie --</option>

                    {groupesList.map((g) => (
                      <option key={g?._id || g?.id} value={g?._id || g?.id}>
                        {g?.nom || "—"}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="adminCategoriesFormGroup">
                  <label className="adminCategoriesLabel">Rang</label>
                  <input
                    type="number"
                    className="adminCategoriesInput"
                    value={form.rang}
                    onChange={(e) =>
                      setForm({ ...form, rang: Number(e.target.value) })
                    }
                    required
                    disabled={loading}
                  />
                </div>

                <div className="adminCategoriesFormGroup">
                  <label className="adminCategoriesLabel">Statut</label>
                  <select
                    className="adminCategoriesSelect"
                    value={String(form.archive)}
                    onChange={(e) =>
                      setForm({ ...form, archive: Number(e.target.value) })
                    }
                    disabled={loading}
                  >
                    <option value="0">Actif</option>
                    <option value="1">Archivé</option>
                  </select>
                </div>

                <div className="adminCategoriesFormGroup">
                  <label className="adminCategoriesLabel">Type</label>
                  <select
                    className="adminCategoriesSelect"
                    value={String(form.premium)}
                    onChange={(e) =>
                      setForm({ ...form, premium: Number(e.target.value) })
                    }
                    disabled={loading}
                  >
                    <option value="1">Premium</option>
                    <option value="2">Standard</option>
                  </select>
                </div>

                <div className="adminCategoriesFormGroup">
                  <label className="adminCategoriesLabel">Prix</label>
                  <select
                    className="adminCategoriesSelect"
                    value={String(form.hasPrice)}
                    onChange={(e) =>
                      setForm({ ...form, hasPrice: Number(e.target.value) })
                    }
                    disabled={loading}
                  >
                    <option value="1">Oui</option>
                    <option value="2">Non</option>
                  </select>
                </div>

                {form.hasPrice === 1 && (
                  <>
                    <div className="adminCategoriesFormGroup">
                      <label className="adminCategoriesLabel">
                        Prix abonnement
                      </label>
                      <input
                        type="number"
                        className="adminCategoriesInput"
                        value={form.prixAbonnement}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            prixAbonnement: Number(e.target.value),
                          })
                        }
                        disabled={loading}
                      />
                    </div>

                    <div className="adminCategoriesFormGroup">
                      <label className="adminCategoriesLabel">Prix boost</label>
                      <input
                        type="number"
                        className="adminCategoriesInput"
                        value={form.prixBoost}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            prixBoost: Number(e.target.value),
                          })
                        }
                        disabled={loading}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="adminCategoriesDivider" />

              {/* Fields */}
              <div className="adminCategoriesSectionHead">
                <div className="adminCategoriesSectionTitle">
                  Champs personnalisés
                </div>
                <button
                  type="button"
                  className="adminCategoriesBtn adminCategoriesBtnSoft"
                  onClick={addField}
                  disabled={loading}
                >
                  <i className="bi bi-plus-lg" />
                  <span>Ajouter</span>
                </button>
              </div>

              {(form.fields || []).length === 0 ? (
                <div className="adminCategoriesHint">
                  Aucun champ personnalisé pour le moment.
                </div>
              ) : (
                <div className="adminCategoriesFieldsWrap">
                  {(form.fields || []).map((f, i) => (
                    <div className="adminCategoriesFieldRow" key={i}>
                      <div className="adminCategoriesFieldLeft">
                        <label className="adminCategoriesLabel">
                          Nom du champ
                        </label>
                        <input
                          className="adminCategoriesInput"
                          value={f.name}
                          onChange={(e) =>
                            updateField(i, "name", e.target.value)
                          }
                          placeholder="Ex: Marque"
                          required
                          disabled={loading}
                        />

                        <label className="adminCategoriesLabel mt8">
                          Icône
                        </label>
                        <div className="adminCategoriesIconPickerWrap">
                          <IconPicker
                            value={f.icon}
                            onSelect={(iconClass) =>
                              updateField(i, "icon", iconClass)
                            }
                          />
                        </div>
                      </div>

                      <div className="adminCategoriesFieldRight">
                        <button
                          type="button"
                          className="adminCategoriesIconBtn danger"
                          title="Supprimer ce champ"
                          onClick={() => removeField(i)}
                          disabled={loading}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="adminCategoriesFormActions">
                <Link
                  to="/admin/categories"
                  className="adminCategoriesBtn adminCategoriesBtnSoft"
                >
                  <i className="bi bi-x-circle" />
                  <span>Annuler</span>
                </Link>

                <button
                  type="submit"
                  className="adminCategoriesBtn adminCategoriesBtnPrimary"
                  disabled={loading}
                >
                  <i className="bi bi-check2-circle" />
                  <span>{loading ? "Enregistrement..." : "Enregistrer"}</span>
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}
